import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Avatar,
  Typography,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
  Dialog,
  DialogContent,
  Fade,
  Stack,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Close,
  Person,
  LocationOn,
  Favorite,
  ChatBubbleOutline,
  MoreVert,
  Flag,
} from '@mui/icons-material';
import { Menu, MenuItem as MuiMenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MessageBubble from './MessageBubble';
import EmptyState from './EmptyState';
import {
  getConversationDetail,
  getMessagesByCursor,
  getNewMessages,
  sendMessage,
  sendMessageToConversation,
  markMessagesAsRead,
  MessageResponse,
  ConversationDetailResponse,
} from '../api/messagesApi';
import { useAuth } from '../../../contexts/AuthContext';
import { parseKSTDate } from '../../../utils/dateUtils';
import { getCardDetail, extendCard } from '../../Dashboard/api/cardsApi';
import type { CupidCardDetail } from '../../Dashboard/types/dashboard';
import { ApiError } from '../../../config/api';

interface MessageDetailPanelProps {
  conversationId: string | null;
  onBack?: () => void;
}

export default function MessageDetailPanel({ conversationId, onBack }: MessageDetailPanelProps) {
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [conversationDetail, setConversationDetail] = useState<ConversationDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [oldestCursor, setOldestCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // 연장 관련 상태
  const [isExtending, setIsExtending] = useState(false);
  const [extendSuccess, setExtendSuccess] = useState(false);

  // chatDeadline 카운트다운용 현재 시각 (1분 갱신)
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // 프로필 모달 관련 상태
  const [profileModal, setProfileModal] = useState(false);
  const [cardDetail, setCardDetail] = useState<CupidCardDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  // API 응답은 userId(number), User 타입은 id(string) — 둘 다 대응
  const currentUserId = String((user as any)?.userId ?? user?.id ?? '');

  // 초기 로드: 대화 상세 + 최신 메시지 (커서 기반)
  const loadConversationData = useCallback(async () => {
    if (!conversationId) return;

    setLoading(true);
    setError(null);
    setMessages([]);
    setHasMore(false);
    setOldestCursor(null);

    // 1. 대화 상세 조회 (실패해도 메시지는 보여줌)
    try {
      const detail = await getConversationDetail(Number(conversationId));
      setConversationDetail(detail);
    } catch (err: any) {
      console.warn('대화 상세 조회 실패:', err.message);
    }

    // 2. 최신 메시지 조회 (커서 기반, cursor 미지정 = 최신부터)
    try {
      const response = await getMessagesByCursor(Number(conversationId));
      // 서버는 최신순 반환 → 시간순 정렬 (오래된 것 먼저)
      const sorted = [...(response.content || [])].sort((a, b) => a.id - b.id);
      setMessages(sorted);
      setHasMore(response.hasNext);
      // nextCursor = 다음 이전 메시지 로드 커서
      setOldestCursor(response.nextCursor ?? null);
    } catch (err: any) {
      setError(err.message || '메시지를 불러올 수 없습니다.');
      setLoading(false);
      return;
    }

    // 3. 읽음 처리 (실패해도 무시)
    try {
      await markMessagesAsRead(Number(conversationId));
    } catch {
      // 무시
    }

    setLoading(false);
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) {
      loadConversationData();
    }
  }, [conversationId, loadConversationData]);

  // 초기 로드 완료 시 최신 메시지로 스크롤
  useEffect(() => {
    if (!loading && messages.length > 0 && conversationId) {
      // DOM 렌더링 완료 후 스크롤 (초기 진입 시에만)
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, conversationId]); // messages는 의존성에서 제외 (초기 로드 시에만 실행)

  // 이전 메시지 로드 (위로 스크롤 시)
  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || !hasMore || loadingOlder || !oldestCursor) return;

    setLoadingOlder(true);
    const container = scrollContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    try {
      const response = await getMessagesByCursor(
        Number(conversationId),
        oldestCursor,
        50
      );
      const olderMessages = [...(response.content || [])].sort((a, b) => a.id - b.id);

      if (olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        setOldestCursor(response.nextCursor ?? null);
        // 스크롤 위치 보정: 이전 메시지 추가 후 현재 보고 있는 위치 유지
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }
        });
      }
      setHasMore(response.hasNext);
    } catch {
      // 에러 시 다음 시도에 재시도
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, hasMore, loadingOlder, oldestCursor]);

  // IntersectionObserver: 상단 센티널이 보이면 이전 메시지 로드
  useEffect(() => {
    const sentinel = messagesTopRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingOlder) {
          loadOlderMessages();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px 0px 0px 0px', // 상단 200px 전에 미리 로드
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingOlder, loadOlderMessages]);

  // Short polling: 5초마다 새 메시지만 효율적으로 확인 (afterCursor 사용)
  const CHAT_POLL_INTERVAL = 5000;
  const messagesRef = useRef<MessageResponse[]>([]);
  messagesRef.current = messages;

  useEffect(() => {
    if (!conversationId) return;

    const poll = setInterval(async () => {
      if (document.hidden) return; // 탭 비활성 시 스킵
      try {
        const currentMessages = messagesRef.current;
        // 마지막 메시지 ID 이후의 새 메시지만 조회
        const lastMsgId = currentMessages.length > 0 ? currentMessages[currentMessages.length - 1].id : null;
        if (!lastMsgId) return; // 메시지가 없으면 스킵 (초기 로드가 처리)

        const response = await getNewMessages(Number(conversationId), lastMsgId);
        const newMsgs = (response.content || []).sort((a, b) => a.id - b.id);

        if (newMsgs.length > 0) {
          // 중복 체크: 기존 메시지 ID와 비교하여 새것만 추가
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const uniqueNewMsgs = newMsgs.filter(m => !existingIds.has(m.id));
            return uniqueNewMsgs.length > 0 ? [...prev, ...uniqueNewMsgs] : prev;
          });
          // 새 메시지 있으면 읽음 처리
          await markMessagesAsRead(Number(conversationId)).catch(() => {});
        }
      } catch {
        // 에러 시 다음 interval에 재시도
      }
    }, CHAT_POLL_INTERVAL);

    return () => clearInterval(poll);
  }, [conversationId]);

  // 최신 메시지 추가 시 하단으로 스크롤
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    // 메시지가 추가된 경우에만 (위로 스크롤로 이전 메시지 로드 시는 제외)
    if (messages.length > prevMsgCountRef.current) {
      const added = messages.length - prevMsgCountRef.current;
      // 이전 메시지 로드가 아닌 경우 (새 메시지 또는 초기 로드)
      const newestMsg = messages[messages.length - 1];
      const prevNewest = prevMsgCountRef.current > 0 ? messages[prevMsgCountRef.current - 1] : null;
      if (!prevNewest || newestMsg?.id !== prevNewest?.id) {
        messagesEndRef.current?.scrollIntoView({ behavior: added > 5 ? 'auto' : 'smooth' });
      }
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);

  const handleSend = async () => {
    if (!inputMessage.trim() || !conversationId) return;

    try {
      let newMessage: MessageResponse;
      if (conversationDetail?.cupidCardId) {
        // cupidCardId 있으면 (카드 수신자) cupidCardId + receiverUserId로 전송
        newMessage = await sendMessage(
          conversationDetail.cupidCardId,
          conversationDetail.otherUserId,
          inputMessage.trim()
        );
      } else {
        // cupidCardId 없으면 (카드 비수신자 또는 detail 없음) conversationId 기반으로 전송
        newMessage = await sendMessageToConversation(
          Number(conversationId),
          inputMessage.trim()
        );
      }

      // 메시지 목록에 추가
      setMessages(prev => [...prev, newMessage]);
      setInputMessage('');
    } catch (err: any) {
      // ApiError 타입 체크 및 errorType 추출
      const isApiError = err instanceof ApiError;
      const errorType = isApiError ? err.errorType : null;
      const errorCode = isApiError ? err.errorCode : null;

      // GTH_4090 (ACQUIRE_LOCK_FAILED): 락 획득 실패 - 스낵바 없이 조용히 무시
      // 사용자가 짧은 시간에 여러 번 전송 시도 시 발생 가능 (정상 상황)
      if (errorType === 'ACQUIRE_LOCK_FAILED' || errorCode === 'GTH_4090') {
        console.log('락 획득 실패 - 재시도 권장');
        return; // 스낵바를 띄우지 않음
      }

      // 서버 커스텀 에러코드별 분기 처리
      if (errorType === 'CARD_EXPIRED' || errorCode === 'GTH_4021') {
        setSendError('큐피트카드가 만료되어 더 이상 쪽지를 보낼 수 없습니다.');
        // 연장 가능 여부 반영을 위해 대화 상세 갱신
        try {
          const detail = await getConversationDetail(Number(conversationId));
          setConversationDetail(detail);
        } catch { /* 무시 */ }
      } else if (errorType === 'INVALID_REQUEST' || errorCode === 'GTH_4000') {
        setSendError('큐피트카드가 만료되어 더 이상 쪽지를 보낼 수 없습니다.');
      } else if (errorType === 'UNAUTHORIZED_REQUEST' || errorCode === 'GTH_4030') {
        setSendError('권한이 없습니다. 다시 로그인해주세요.');
      } else if (errorType === 'NOT_FOUND' || errorCode === 'GTH_4040') {
        setSendError('대화를 찾을 수 없습니다.');
      } else if (errorType === 'NOT_FOUND_USER' || errorCode === 'GTH_4041') {
        setSendError('존재하지 않는 사용자입니다.');
      } else if (errorType === 'INTERNAL_SERVER_ERROR' || errorCode === 'GTH_5000') {
        setSendError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } else if (isApiError && err.status >= 500) {
        // HTTP 500번대 에러 (서버 에러)
        setSendError('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
      } else if (isApiError && err.status === 429) {
        // HTTP 429 Too Many Requests
        setSendError('너무 많은 요청을 보냈습니다. 잠시 후 다시 시도해주세요.');
      } else {
        // 기타 에러 - 서버 메시지 또는 기본 메시지
        setSendError(err.message || '메시지 전송에 실패했습니다.');
      }
    }
  };

  const handleProfileClick = async () => {
    if (!conversationDetail?.cupidCardId) {
      // cupidCardId가 없으면 프로필을 불러올 수 없음
      return;
    }

    // 모달 먼저 열기 (즉시 피드백)
    setProfileModal(true);
    setDetailError(null);
    setCardDetail(null);
    setSelectedImageIndex(0);
    setLoadingDetail(true);

    try {
      const detail = await getCardDetail(String(conversationDetail.cupidCardId));
      setCardDetail(detail);
      setDetailError(null);
    } catch (error: any) {
      setDetailError(error?.message || "프로필 정보를 불러올 수 없습니다.");
      setCardDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleExtendFromChat = async () => {
    if (!conversationDetail?.cupidCardId || isExtending) return;
    setIsExtending(true);
    try {
      await extendCard(String(conversationDetail.cupidCardId));
      setExtendSuccess(true);
      setMessages(prev => [...prev, {
        id: Date.now(),
        conversationId: Number(conversationId),
        senderUserId: 0,
        content: '대화가 일요일 11시까지 연장되었어요 🎉',
        createdAt: new Date().toISOString(),
        isRead: true,
      } as any]);
      // 대화 상세 재조회로 상태 갱신
      const updated = await getConversationDetail(Number(conversationId));
      setConversationDetail(updated);
    } catch (err: any) {
      setSendError(err.message || '카드 연장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsExtending(false);
    }
  };

  if (!conversationId) {
    return (
      <Paper elevation={1} sx={{ height: '100%', width: '100%', display: 'flex' }}>
        <EmptyState variant="no-selection" />
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper elevation={1} sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 2, height: '100%', width: '100%' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!conversationDetail && messages.length === 0) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 헤더 - 상단 고정 */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          zIndex: 10,
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          {/* 뒤로가기 (모바일만) */}
          {onBack && (
            <IconButton
              edge="start"
              onClick={onBack}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          {/* 프로필 */}
          <Avatar
            src={conversationDetail?.otherUserProfileImageUrl || undefined}
            onClick={handleProfileClick}
            sx={{
              width: 44,
              height: 44,
              bgcolor: '#E8CFA0',
              cursor: conversationDetail?.cupidCardId ? 'pointer' : 'default',
              '&:hover': conversationDetail?.cupidCardId ? {
                opacity: 0.8,
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              } : {},
            }}
          >
            {conversationDetail?.otherUserNickname?.[0] || '?'}
          </Avatar>

          {/* 이름 */}
          <Box
            sx={{
              flex: 1,
              cursor: conversationDetail?.cupidCardId ? 'pointer' : 'default',
            }}
            onClick={handleProfileClick}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {conversationDetail?.otherUserNickname || '대화'}
            </Typography>
            {conversationDetail?.isExpired && (
              <Typography variant="caption" color="error">
                큐피트 카드 만료됨
              </Typography>
            )}
          </Box>

          {/* 만료 칩 */}
          {conversationDetail?.isExpired && (
            <Chip label="만료" color="error" size="small" />
          )}

          {/* 더보기 메뉴 */}
          <IconButton
            edge="end"
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
            aria-label="더보기"
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={() => setMoreMenuAnchor(null)}
          >
            <MuiMenuItem
              onClick={() => {
                setMoreMenuAnchor(null);
                const targetId = conversationDetail?.otherUserId;
                const cardId = conversationDetail?.cupidCardId;
                const qs = new URLSearchParams();
                if (targetId) qs.append('targetId', String(targetId));
                if (cardId) qs.append('cardId', String(cardId));
                navigate(`/report?${qs.toString()}`);
              }}
              sx={{ color: 'error.main', gap: 1 }}
            >
              <Flag fontSize="small" />
              신고하기
            </MuiMenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* chatDeadline 카운트다운 인포바 (D-3 이하) */}
      {(() => {
        const deadline = conversationDetail?.chatDeadline;
        if (!deadline) return null;
        const ms = parseKSTDate(deadline).getTime() - now.getTime();
        if (ms <= 0) return null;
        const daysLeft = ms / (24 * 60 * 60 * 1000);
        if (daysLeft > 3) return null;
        const color = daysLeft <= 1 ? '#E53E3E' : '#FF8C00';
        const label = daysLeft <= 1
          ? `D-${Math.ceil(daysLeft)} · 내일까지만 대화할 수 있어요`
          : `D-${Math.ceil(daysLeft)} · ${Math.ceil(daysLeft)}일 안에 대화가 종료돼요`;
        return (
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 5,
            bgcolor: `${color}14`, borderLeft: `4px solid ${color}`,
            px: 2, py: 1, flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <AccessTimeIcon sx={{ fontSize: 16, color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color }}>
              {label}
            </Typography>
          </Box>
        );
      })()}

      {/* 만료/DEAD 상태 배너 - 상단 고정 */}
      {conversationDetail?.isExpired && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            borderLeft: conversationDetail.isExtendable ? '4px solid #FF6B35' : '4px solid #9E9E9E',
            bgcolor: conversationDetail.isExtendable ? '#FFF3E0' : '#F5F5F5',
            px: 2,
            py: 1.5,
            flexShrink: 0,
          }}
        >
          {conversationDetail.isExtendable ? (
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#E65100' }}>
              이 대화가 만료되었어요
              <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.8125rem', color: '#9A3412', ml: 1 }}>
                일요일 11시까지 연장할 수 있어요
              </Typography>
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#616161' }}>
                대화가 종료되었어요
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#757575', mt: 0.25 }}>
                다음 주 새로운 카드를 기다려주세요.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* 메시지 목록 - 스크롤 가능, 메시지 아래 정렬 */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: '#F5F7FA',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1 }} /> {/* 메시지가 적을 때 위쪽 빈 공간 */}
        <Box sx={{ p: 2 }}>
          {/* 상단 센티널: IntersectionObserver가 이전 메시지 로드 트리거 */}
          <div ref={messagesTopRef} />
          {loadingOlder && (
            <Box textAlign="center" py={1}>
              <CircularProgress size={24} />
            </Box>
          )}

          {messages.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                아직 메시지가 없습니다
              </Typography>
            </Box>
          ) : (
            messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={{
                  id: String(msg.id),
                  conversationId: String(msg.conversationId),
                  senderId: String(msg.senderUserId),
                  content: msg.content,
                  createdAt: parseKSTDate(msg.createdAt),
                  isRead: msg.isRead,
                }}
                isOwn={String(msg.senderUserId) === currentUserId}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* 입력 영역 */}
      {!conversationDetail?.isExpired ? (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white', flexShrink: 0, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="메시지를 입력하세요..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '1rem',
                  borderRadius: 3,
                  bgcolor: 'grey.50',
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  px: 2,
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!inputMessage.trim()}
              sx={{
                width: 56,
                height: 56,
                bgcolor: inputMessage.trim() ? 'primary.main' : 'grey.300',
                color: 'white',
                '&:hover': {
                  bgcolor: inputMessage.trim() ? 'primary.dark' : 'grey.400',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'white',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: conversationDetail?.isExtendable ? '#FFF3E0' : 'grey.50',
            flexShrink: 0,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {conversationDetail?.isExtendable ? (
            // EXPIRED_EXTENDABLE: 연장 유도 배너
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AccessTimeIcon sx={{ color: 'error.main', mt: 0.25, flexShrink: 0, fontSize: 22 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#E65100', lineHeight: 1.3 }}>
                    대화 기간이 종료되었어요
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#8896AB', mt: 0.5 }}>
                    {conversationDetail.extendDeadlineAt
                      ? `일요일 11:00까지 연장할 수 있어요`
                      : '이번 주 일요일까지 연장할 수 있어요'}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AccessTimeIcon />}
                onClick={handleExtendFromChat}
                disabled={isExtending || extendSuccess}
                fullWidth
                sx={{
                  height: 52,
                  fontSize: '1rem',
                  fontWeight: 800,
                  borderRadius: 2,
                  boxShadow: 2,
                  bgcolor: '#FF6B35',
                  color: 'white',
                  '&:hover': { bgcolor: '#E55B2A', boxShadow: 4 },
                  '&.Mui-disabled': { bgcolor: '#FF6B3580', color: 'white' },
                }}
              >
                {extendSuccess
                  ? '✓ 연장 완료!'
                  : isExtending
                  ? '연장 중...'
                  : `⏰ 연장하기 (하트 ${conversationDetail.extendCostHearts ?? 3}개)`}
              </Button>
            </Stack>
          ) : (
            // DEAD: 완전 만료
            <Box sx={{ bgcolor: '#F5F5F5', borderLeft: '4px solid #9E9E9E', px: 2, py: 1.5, borderRadius: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#616161' }}>대화가 종료되었어요</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#757575', mt: 0.25 }}>다음 주 새로운 카드를 기다려주세요.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* 메시지 전송 에러 Snackbar */}
      <Snackbar
        open={!!sendError}
        autoHideDuration={4000}
        onClose={() => setSendError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSendError(null)}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {sendError}
        </Alert>
      </Snackbar>

      {/* 프로필 모달 */}
      <Dialog
        open={profileModal}
        onClose={() => {
          setProfileModal(false);
          setCardDetail(null);
          setDetailError(null);
          setSelectedImageIndex(0);
        }}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            maxHeight: { xs: '100vh', sm: '95vh' },
            m: { xs: 0, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* 헤더 (모바일 전용) */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            flexShrink: 0
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            프로필
          </Typography>
          <IconButton
            onClick={() => {
              setProfileModal(false);
              setCardDetail(null);
              setDetailError(null);
              setSelectedImageIndex(0);
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1, minHeight: 0 }}>
          {loadingDetail ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={48} />
              <Typography sx={{ mt: 2 }}>프로필 정보를 불러오는 중...</Typography>
            </Box>
          ) : cardDetail ? (
            <Box>
              {/* 프로필 이미지 갤러리 */}
              <Box sx={{ position: 'relative' }}>
                {/* 메인 이미지 */}
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={cardDetail.targetProfile.profilePhotos[selectedImageIndex]?.endpointUrl || cardDetail.thumbnailImage.photoUrl}
                    alt={`${cardDetail.targetProfile.name} 프로필`}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxHeight: { xs: 450, sm: 550, md: 600 },
                      objectFit: 'contain',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      const nextIndex = (selectedImageIndex + 1) % Math.max(cardDetail.targetProfile.profilePhotos.length, 1);
                      setSelectedImageIndex(nextIndex);
                    }}
                  />

                  {/* 이미지 오버레이 (데스크톱 전용) */}
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      position: 'absolute',
                      top: 16,
                      right: 16,
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        setProfileModal(false);
                        setCardDetail(null);
                        setDetailError(null);
                        setSelectedImageIndex(0);
                      }}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Box>

                  {/* 기본 정보 오버레이 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: { xs: 2, sm: 3 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontWeight: 800,
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                      }}
                    >
                      {cardDetail.targetProfile.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 1 }}>
                      {cardDetail.targetProfile.age}세 · {cardDetail.targetProfile.residenceRegion.level1Name} {cardDetail.targetProfile.residenceRegion.level2Name}
                    </Typography>
                    {cardDetail.targetProfile.jobTitle && (
                      <Chip
                        label={cardDetail.targetProfile.jobTitle}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          color: 'text.primary',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* 추가 프로필 이미지 (있는 경우) */}
                {cardDetail.targetProfile.profilePhotos.length > 1 && (
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    p: { xs: 2, sm: 3 },
                    overflowX: 'auto',
                    scrollbarWidth: 'thin'
                  }}>
                    {cardDetail.targetProfile.profilePhotos.map((photo, index) => {
                      const isSelected = index === selectedImageIndex;
                      return (
                        <Box
                          key={photo.id}
                          sx={{
                            flex: '0 0 auto',
                            width: { xs: 80, sm: 100 },
                            height: { xs: 80, sm: 100 },
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '3px solid',
                            borderColor: isSelected ? 'primary.main' : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            opacity: isSelected ? 1 : 0.7,
                            transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                            '&:hover': {
                              opacity: 1,
                              transform: 'scale(1.05)'
                            }
                          }}
                          onClick={() => setSelectedImageIndex(index)}
                        >
                          <Box
                            component="img"
                            src={photo.endpointUrl}
                            alt={`${cardDetail.targetProfile.name} 프로필 ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* 프로필 정보 */}
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                  {/* 기본 정보 카드 */}
                  <Box sx={{ bgcolor: 'grey.50', p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1, color: 'primary.main' }} />
                      기본 정보
                    </Typography>

                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">나이</Typography>
                        <Typography variant="body1" fontWeight={600}>{cardDetail.targetProfile.age}세</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">성별</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {cardDetail.targetProfile.gender === 'MALE' ? '남성' : '여성'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">거주지</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {cardDetail.targetProfile.residenceRegion.level1Name} {cardDetail.targetProfile.residenceRegion.level2Name}
                        </Typography>
                      </Box>

                      {cardDetail.targetProfile.jobTitle && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">직업</Typography>
                          <Typography variant="body1" fontWeight={600}>{cardDetail.targetProfile.jobTitle}</Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">선호 연령대</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {cardDetail.targetProfile.preferredAge === 'YOUNGER' ? '연하' :
                           cardDetail.targetProfile.preferredAge === 'OLDER' ? '연상' : '상관없음'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* 한줄소개 */}
                  {cardDetail.targetProfile.oneLiner && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <ChatBubbleOutline sx={{ mr: 1, color: 'secondary.main' }} />
                        한줄소개
                      </Typography>
                      <Box sx={{ bgcolor: 'rgba(201,168,108,0.08)', p: 2.5, borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                          "{cardDetail.targetProfile.oneLiner}"
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* 자기소개 */}
                  {cardDetail.targetProfile.selfIntro && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                        자기소개
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                        {cardDetail.targetProfile.selfIntro}
                      </Typography>
                    </Box>
                  )}

                  {/* 관심사 */}
                  {cardDetail.targetProfile.interests && cardDetail.targetProfile.interests.length > 0 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <Favorite sx={{ mr: 1, color: 'error.main' }} />
                        관심사
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {cardDetail.targetProfile.interests.map((interest) => (
                          <Chip
                            key={interest.id}
                            label={interest.name}
                            sx={{
                              fontWeight: 600,
                              bgcolor: 'primary.100',
                              color: 'primary.700',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* 선호 지역 */}
                  {cardDetail.targetProfile.preferredRegions && cardDetail.targetProfile.preferredRegions.length > 0 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ mr: 1, color: 'success.main' }} />
                        선호 지역
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {cardDetail.targetProfile.preferredRegions.map((region, index) => (
                          <Chip
                            key={`${region.level1Id}-${region.level2Id}-${index}`}
                            label={`${region.level1Name} ${region.level2Name}`}
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              borderColor: 'success.main',
                              color: 'success.main',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          ) : detailError ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="h6" sx={{ mb: 2 }}>
                {detailError}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
